import { useEffect, useState } from "react";

import { changePassword } from "../../api/auth";
import { useAuth } from "../../context/AuthContext";
import LoadingButton from "../../components/LoadingButton";
import { errorMessage } from "../../utils/format";

/**
 * The forced password change (technical-foundation §16 / MDS §16).
 *
 * ── WHY THIS SCREEN EXISTS ──────────────────────────────────────────────────
 * `requirePasswordChanged` (middleware/authorize.js) answers 403 "Password change required before
 * continuing" on EVERY protected route while `mustChangePassword` is true. Only three endpoints are
 * exempt, by deliberate design: `/auth/me`, `/auth/logout` and `/auth/change-password`. This panel
 * had no screen for the third, so an account created by `scripts/bootstrap-admin.js` — which sets
 * `mustChangePassword: true` — could sign in and then do nothing at all, with no way out.
 *
 * ── IT IS RENDERED IN PLACE, NOT ROUTED TO ──────────────────────────────────
 * `ProtectedRoute` returns this INSTEAD of the `<Outlet/>`, exactly as it already does for a customer
 * account reaching the staff panel. That means there is no `/change-password` URL to bookmark, no
 * redirect to race, and no route a flagged user can navigate around — every protected path renders
 * this and nothing else. It is a gate, not a destination.
 *
 * ── NO RE-LOGIN IS NEEDED AFTERWARDS ────────────────────────────────────────
 * `authService.changePassword` clears the flag on the user record; it does not revoke the session,
 * and `protect` re-reads the user from the database on every request. So the existing `__session`
 * cookie stays valid and `refresh()` alone re-opens the panel. Signing the user out here would be a
 * second, invented step.
 */
export default function ForcePasswordChange() {
  useEffect(() => {
    document.title = "VVR ERP | Change your password";
  }, []);

  const { user, logout, refresh } = useAuth();

  const [form, setForm] = useState({ currentPassword: "", newPassword: "", confirm: "" });
  const [errors, setErrors] = useState({});
  const [failure, setFailure] = useState("");
  const [saving, setSaving] = useState(false);

  const set = (field) => (e) => {
    setForm((f) => ({ ...f, [field]: e.target.value }));
    setErrors((er) => ({ ...er, [field]: undefined }));
    setFailure("");
  };

  /**
   * Mirrors `utils/passwordPolicy.js` exactly — min 8, and one each of upper, lower, digit and
   * special. Stated up front so the requirement is met before the round trip; the backend re-checks
   * and its message wins on any disagreement.
   */
  const validate = () => {
    const e = {};
    if (!form.currentPassword) e.currentPassword = "Enter your current (temporary) password";

    const missing = [];
    if (form.newPassword.length < 8) missing.push("at least 8 characters");
    if (!/[A-Z]/.test(form.newPassword)) missing.push("one uppercase letter");
    if (!/[a-z]/.test(form.newPassword)) missing.push("one lowercase letter");
    if (!/[0-9]/.test(form.newPassword)) missing.push("one number");
    if (!/[^A-Za-z0-9]/.test(form.newPassword)) missing.push("one special character");
    if (missing.length) e.newPassword = `Password must contain ${missing.join(", ")}`;
    else if (form.newPassword === form.currentPassword) {
      e.newPassword = "New password must be different from the current password";
    }

    if (form.newPassword !== form.confirm) e.confirm = "Passwords do not match";

    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const submit = async (ev) => {
    ev.preventDefault();
    if (!validate()) return;
    try {
      setSaving(true);
      await changePassword(form.currentPassword, form.newPassword);
      // The server has cleared the flag; re-asking is what re-opens the panel. `refresh()` is the
      // same `/auth/me` call the provider makes on boot, so there is one source of truth for it.
      await refresh();
    } catch (err) {
      const data = err?.response?.data;
      if (data?.errors) setErrors(data.errors);
      else setFailure(errorMessage(err, "Your password could not be changed"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="d-flex vh-100 align-items-center justify-content-center p-3">
      <div className="w-100" style={{ maxWidth: 420 }}>
        <div className="text-center mb-3">
          <i className="bi bi-shield-lock" style={{ fontSize: 30, opacity: 0.6 }} />
          <h1 className="h5 mt-2 mb-0">Choose a new password</h1>
          <p className="text-muted small mb-0">
            {user?.fullName ? `${user.fullName} — your ` : "Your "}
            account is using a temporary password and must set a new one before continuing.
          </p>
        </div>

        <div className="card border-0 shadow-sm">
          <div className="card-body p-4">
            {failure && <div className="alert alert-danger py-2 small">{failure}</div>}

            <form onSubmit={submit} noValidate>
              <div className="mb-3">
                <label className="form-label" htmlFor="fpc-current">
                  Current password
                </label>
                <input
                  id="fpc-current"
                  type="password"
                  autoComplete="current-password"
                  className={`form-control ${errors.currentPassword ? "is-invalid" : ""}`}
                  value={form.currentPassword}
                  onChange={set("currentPassword")}
                  autoFocus
                />
                {errors.currentPassword && (
                  <div className="invalid-feedback">{errors.currentPassword}</div>
                )}
              </div>

              <div className="mb-3">
                <label className="form-label" htmlFor="fpc-new">
                  New password
                </label>
                <input
                  id="fpc-new"
                  type="password"
                  autoComplete="new-password"
                  className={`form-control ${errors.newPassword ? "is-invalid" : ""}`}
                  value={form.newPassword}
                  onChange={set("newPassword")}
                />
                {errors.newPassword ? (
                  <div className="invalid-feedback">{errors.newPassword}</div>
                ) : (
                  <div className="form-text">
                    At least 8 characters, with an uppercase letter, a lowercase letter, a number and
                    a special character.
                  </div>
                )}
              </div>

              <div className="mb-4">
                <label className="form-label" htmlFor="fpc-confirm">
                  Confirm new password
                </label>
                <input
                  id="fpc-confirm"
                  type="password"
                  autoComplete="new-password"
                  className={`form-control ${errors.confirm ? "is-invalid" : ""}`}
                  value={form.confirm}
                  onChange={set("confirm")}
                />
                {errors.confirm && <div className="invalid-feedback">{errors.confirm}</div>}
              </div>

              <LoadingButton
                type="submit"
                className="btn btn-primary w-100"
                loading={saving}
                loadingText="Saving…"
              >
                Set new password
              </LoadingButton>
            </form>
          </div>
        </div>

        {/* Signing out is the only other thing `requirePasswordChanged` permits, so it is the only
            other action offered. Anything else on this screen would be a door that 403s. */}
        <div className="text-center mt-3">
          <button className="btn btn-sm btn-link text-muted" disabled={saving} onClick={logout}>
            Sign out instead
          </button>
        </div>
      </div>
    </div>
  );
}
